import { Button } from "@babackapp/customer-ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import type {
  CatalogProduct,
  CatalogProductVariant,
} from "@babackapp/private-api-client/client/use-catalog-product";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { PlusIcon } from "lucide-react";
import { useProductVariantPicker } from "../../../hooks/use-product-variant-picker";

type Props = {
  product: CatalogProduct;
  fallbackImageUrl: string | null;
  isAdding: boolean;
  onAdd: (variant: CatalogProductVariant, imageUrl: string | null) => void;
};

/**
 * Photo + variant selects for a loaded catalogue product. Mount keyed on the
 * product id so `useProductVariantPicker` re-initialises its first-variant
 * default whenever a different product is picked.
 */
export function CatalogVariantPicker({
  product,
  fallbackImageUrl,
  isAdding,
  onAdd,
}: Props) {
  const t = useCustomText();
  const { selectedOptions, selectedVariant, selectableOptions, handleOptionChange } =
    useProductVariantPicker(product.options, product.variants);

  const imageUrl =
    selectedVariant?.imageUrl ?? product.featuredImageUrl ?? fallbackImageUrl;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center">
        <div className="size-32 overflow-hidden rounded-lg bg-muted">
          {imageUrl && (
            <img src={imageUrl} alt={product.title} className="size-full object-cover" />
          )}
        </div>
      </div>

      {selectableOptions.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2">
          {selectableOptions.map((opt) => (
            <Select
              key={opt.name}
              value={selectedOptions[opt.name] ?? ""}
              onValueChange={(v) => handleOptionChange(opt.name, v)}
            >
              <SelectTrigger className="w-auto">
                <SelectValue placeholder={opt.name} />
              </SelectTrigger>
              <SelectContent>
                {opt.values.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
        </div>
      )}

      <Button
        type="button"
        className="w-full"
        disabled={!selectedVariant || isAdding}
        onClick={() => selectedVariant && onAdd(selectedVariant, imageUrl)}
      >
        <PlusIcon />
        {t("catalogPicker.add")}
      </Button>
    </div>
  );
}
